import { useState, useRef, useEffect } from "react";
import { ChevronDown, Search } from "lucide-react";
import { useMarket, MARKETS } from "@/context/MarketContext";

/**
 * MarketSwitcher — compact dropdown in the shared header.
 * Search by Arabic name, ISO2, or country calling code (+971, +966, …).
 * Calling code is a search shortcut only — it never geolocates the user.
 */
export const MarketSwitcher = ({ testId = "market-switcher" }) => {
  const { market, setMarket } = useMarket();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const filtered = MARKETS.filter((m) => m.enabled).filter((m) => {
    if (!q.trim()) return true;
    const needle = q.trim().toLowerCase();
    return (
      m.ar.toLowerCase().includes(needle) ||
      m.iso2.toLowerCase().includes(needle) ||
      m.calling.replace("+", "").startsWith(needle.replace("+", ""))
    );
  });

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        data-testid={testId}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`السوق الحالي: ${market.ar}`}
        title={`السوق الحالي: ${market.ar}`}
        className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-surface-200 bg-white px-2.5 text-xs font-medium text-ink-700 shadow-sm transition-colors hover:border-fursa-300 hover:text-fursa-700"
      >
        <span aria-hidden className="text-base leading-none">{market.flag}</span>
        <span className="hidden sm:inline">{market.ar}</span>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={1.75} />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute end-0 top-11 z-50 w-64 overflow-hidden rounded-xl border border-surface-200 bg-white shadow-lg"
        >
          <div className="border-b border-surface-100 p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute end-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-500" strokeWidth={1.75} />
              <input
                type="search"
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="ابحث… (اسم أو +رمز الاتصال)"
                data-testid="market-switcher-search"
                className="h-8 w-full rounded-lg border border-surface-200 bg-white ps-2.5 pe-7 text-xs outline-none placeholder:text-ink-500/60 focus:border-fursa-500 focus:ring-2 focus:ring-fursa-500/15"
              />
            </div>
          </div>
          <ul className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-xs text-ink-500">لا نتائج</li>
            )}
            {filtered.map((m) => {
              const active = m.iso2 === market.iso2;
              return (
                <li key={m.iso2}>
                  <button
                    type="button"
                    onClick={() => { setMarket(m.iso2); setOpen(false); setQ(""); }}
                    data-testid={`market-option-${m.iso2}`}
                    aria-selected={active}
                    className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-right text-xs transition-colors ${active ? "bg-fursa-50 font-semibold text-fursa-800" : "text-ink-700 hover:bg-surface-50"}`}
                  >
                    <span className="flex items-center gap-2">
                      <span aria-hidden className="text-base leading-none">{m.flag}</span>
                      <span>{m.ar}</span>
                    </span>
                    <span dir="ltr" className="font-mono text-[10px] text-ink-500">{m.calling}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default MarketSwitcher;
